import { useEffect, useState } from "react";
import { ChevronDown, Lock, ShieldCheck } from "lucide-react";
import { DsAlert, DsIcon } from "../../../design-system";
import { ADMIN_RBAC_CATALOG, groupScreensForAccess } from "../../../rbac/catalog";
import {
  ACTION_HINTS,
  ACTION_SHORT_LABELS,
  applyScreenPreset,
  moduleStats,
  orderedActions,
  screenGrantCount,
  toggleScreenAction,
} from "./roleAccessUtils";

const PRESETS = [
  { id: "none", label: "None" },
  { id: "view", label: "View only" },
  { id: "full", label: "All" },
];

const cleanWhere = (where) => String(where || "").replace(/^\(|\)$/g, "").trim();

function PresetButtons({ onApply, disabled, size = "sm", label }) {
  return (
    <div role="group" aria-label={label} className="inline-flex shrink-0 rounded-md border border-[hsl(var(--ds-border))] p-0.5">
      {PRESETS.map((p) => (
        <button
          key={p.id}
          type="button"
          disabled={disabled}
          onClick={() => onApply(p.id)}
          className={`rounded px-2 ${size === "md" ? "py-1 text-xs" : "py-0.5 text-[11px]"} font-medium text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function ScreenRow({ screen, perms, disabled, onToggle }) {
  const [open, setOpen] = useState(false);
  const actions = orderedActions(screen);
  const granted = screenGrantCount(perms, screen) > 0;
  return (
    <li
      className={`rounded-lg border px-3 py-2.5 ${
        granted ? "border-[hsl(var(--ds-primary)/0.35)] bg-[hsl(var(--ds-primary)/0.04)]" : "border-[hsl(var(--ds-border))]"
      }`}
    >
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="min-w-[180px] flex-1">
          <div className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{screen.label}</div>
          {screen.description ? (
            <p className="mt-0.5 text-xs leading-snug text-[hsl(var(--ds-muted-fg))]">{screen.description}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {actions.map((action) => {
            const on = perms?.[action] === true;
            const specific = screen.actionLabels?.[action];
            const where = cleanWhere(screen.actionWhere?.[action]);
            const tip = [specific || ACTION_HINTS[action], where].filter(Boolean).join(" — ");
            return (
              <button
                key={action}
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-label={`${ACTION_SHORT_LABELS[action]}: ${specific || screen.label}`}
                title={tip}
                disabled={disabled}
                onClick={() => onToggle(action, !on)}
                className={`inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none ${
                  on
                    ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:border-[hsl(var(--ds-primary)/0.5)]"
                }`}
              >
                {ACTION_SHORT_LABELS[action]}
              </button>
            );
          })}
        </div>
      </div>
      {actions.length ? (
        <>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--ds-primary))] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
          >
            <DsIcon icon={ChevronDown} size="sm" className={`h-3 w-3 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
            What does each option allow?
          </button>
          {open ? (
            <ul className="mt-1.5 space-y-1 border-l-2 border-[hsl(var(--ds-border))] pl-3">
              {actions.map((action) => (
                <li key={action} className="text-[11px] leading-snug text-[hsl(var(--ds-fg))]">
                  <span className="font-semibold">{ACTION_SHORT_LABELS[action]}:</span>{" "}
                  {screen.actionLabels?.[action] || ACTION_HINTS[action]}
                  {cleanWhere(screen.actionWhere?.[action]) ? (
                    <span className="text-[hsl(var(--ds-muted-fg))]"> — {cleanWhere(screen.actionWhere[action])}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </li>
  );
}

function DelegationPicker({ value, onChange, disabled }) {
  const options = ADMIN_RBAC_CATALOG.filter((m) => m.key !== "user_roles");
  const selected = new Set(value);
  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(options.map((m) => m.key).filter((k) => next.has(k)));
  };
  return (
    <section className="mb-4 rounded-xl border border-[hsl(var(--ds-info)/0.35)] bg-[hsl(var(--ds-info)/0.06)] p-4">
      <div className="flex items-start gap-2.5">
        <DsIcon icon={ShieldCheck} size="md" className="mt-0.5 text-[hsl(var(--ds-info))]" />
        <div className="min-w-0 flex-1">
          <h5 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">Modules this role can grant to other roles</h5>
          <p className="mt-0.5 text-xs leading-snug text-[hsl(var(--ds-muted-fg))]">
            Employees with this role can manage roles only for the modules ticked here. They can&apos;t
            edit their own role, see default system roles, grant User Roles, or give data access wider
            than their own. Only an admin can change this list.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {options.map((m) => (
              <label key={m.key} className="flex cursor-pointer items-center gap-2 text-sm text-[hsl(var(--ds-fg))]">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={selected.has(m.key)}
                  onChange={() => toggle(m.key)}
                  className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
                />
                {m.label}
              </label>
            ))}
          </div>
          {value.length === 0 ? (
            <p className="mt-2 text-[11px] text-[hsl(var(--ds-warning-fg))]">
              No module ticked: this role can open User Roles but can&apos;t grant any module.
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default function PermissionsStep({
  catalog,
  access,
  selectedModules,
  lockedModules,
  readOnly,
  error,
  isAdmin,
  delegableModules,
  onChangeDelegable,
  onChangeModule,
}) {
  const modules = catalog.filter((m) => selectedModules.has(m.key));
  const [activeKey, setActiveKey] = useState(modules[0]?.key || null);

  useEffect(() => {
    if (!modules.some((m) => m.key === activeKey)) setActiveKey(modules[0]?.key || null);
  }, [modules, activeKey]);

  const active = modules.find((m) => m.key === activeKey) || null;
  const locked = active ? lockedModules.has(active.key) : false;
  const disabled = readOnly || locked;
  const moduleAcc = active ? access?.[active.key] : null;

  const apply = (screenKeys, preset) => onChangeModule(active.key, applyScreenPreset(moduleAcc, active, screenKeys, preset));

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {error ? <DsAlert tone="danger">{error}</DsAlert> : null}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] md:flex-row">
        <nav aria-label="Selected modules" className="shrink-0 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.4)] md:w-56 md:border-b-0 md:border-r">
          <ul className="flex gap-1 overflow-x-auto p-2 md:flex-col md:overflow-y-auto">
            {modules.map((m) => {
              const s = moduleStats(m, access?.[m.key]);
              const isActive = m.key === activeKey;
              return (
                <li key={m.key} className="shrink-0">
                  <button
                    type="button"
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => setActiveKey(m.key)}
                    className={`w-full rounded-lg px-3 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] ${
                      isActive ? "bg-[hsl(var(--ds-surface))] shadow-sm ring-1 ring-[hsl(var(--ds-border))]" : "hover:bg-[hsl(var(--ds-surface)/0.7)]"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--ds-fg))]">
                      {lockedModules.has(m.key) ? <DsIcon icon={Lock} size="sm" className="h-3 w-3 text-[hsl(var(--ds-muted-fg))]" /> : null}
                      {m.label}
                    </span>
                    <span
                      className={`block text-[11px] ${
                        s.granted === 0 ? "text-[hsl(var(--ds-danger))]" : "text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      {s.granted === 0 ? "Nothing chosen yet" : `${s.screensOn} of ${s.screenTotal} screens`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {active ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex shrink-0 flex-wrap items-start gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
              <div className="min-w-0 flex-1">
                <h4 className="ds-h4 text-[hsl(var(--ds-fg))]">{active.label}</h4>
                {active.description ? (
                  <p className="text-xs text-[hsl(var(--ds-muted-fg))]">{active.description}</p>
                ) : null}
                {locked ? (
                  <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                    <DsIcon icon={Lock} size="sm" className="h-3 w-3" /> Outside the modules you can grant. Shown
                    for reference and saved unchanged.
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[hsl(var(--ds-muted-fg))]">Whole module:</span>
                <PresetButtons
                  size="md"
                  label={`Set all ${active.label} screens`}
                  disabled={disabled}
                  onApply={(p) => apply(active.screens.map((s) => s.key), p)}
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
              {active.key === "user_roles" && isAdmin ? (
                <DelegationPicker value={delegableModules} onChange={onChangeDelegable} disabled={readOnly} />
              ) : null}
              <p className="mb-3 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                View is needed before any other option on a screen. Turning View off clears that
                screen&apos;s other options. Hover an option, or open “What does each option allow?”, for
                the exact button it controls.
              </p>
              <div className="space-y-5">
                {groupScreensForAccess(active.screens).map((group, gi) => {
                  const keys = group.screens.map((s) => s.key);
                  const on = group.screens.filter((s) => screenGrantCount(moduleAcc?.screens?.[s.key], s) > 0).length;
                  return (
                    <section key={`${active.key}-${group.label || gi}`} aria-label={group.label || active.label}>
                      {group.label || group.screens.length > 1 ? (
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <h5 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                            {group.label || "Other"}
                          </h5>
                          <span className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                            {on}/{group.screens.length}
                          </span>
                          <span className="ml-auto">
                            <PresetButtons
                              label={`Set ${group.label || "these"} screens`}
                              disabled={disabled}
                              onApply={(p) => apply(keys, p)}
                            />
                          </span>
                        </div>
                      ) : null}
                      <ul className="space-y-2">
                        {group.screens.map((screen) => (
                          <ScreenRow
                            key={screen.key}
                            screen={screen}
                            perms={moduleAcc?.screens?.[screen.key]}
                            disabled={disabled}
                            onToggle={(action, value) =>
                              onChangeModule(active.key, toggleScreenAction(moduleAcc, active, screen.key, action, value))
                            }
                          />
                        ))}
                      </ul>
                    </section>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center p-8">
            <p className="ds-body text-[hsl(var(--ds-muted-fg))]">Go back and select at least one module.</p>
          </div>
        )}
      </div>
    </div>
  );
}
